import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Bell, BellOff, Check, CornerUpLeft, FileText, Flag, Loader2, Lock,
  MoreHorizontal, Paperclip, Pencil, Pin, Reply as ReplyIcon, Send, ShieldOff,
  SmilePlus, Trash2, X,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { usePresence } from "@/hooks/usePresence";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MentionTextarea } from "@/components/ui/mention-textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { UploadProgressBar } from "@/components/ui/upload-progress-bar";
import { OnlineUsersBadge } from "@/components/OnlineUsersBadge";
import { ReportPostDialog } from "@/components/ReportPostDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  useCreatePost,
  useDeletePost,
  useDeleteThread,
  useLockThread,
  usePinThread,
  usePosts,
  useReactionsCatalog,
  useReactToPost,
  useSubscribeThread,
  useThread,
  useThreadSocket,
  useUnsubscribeThread,
  useUpdatePost,
} from "@/hooks/useForum";
import { formatDateTime, initials } from "@/lib/format";
import {
  REACTION_EMOJIS,
  type PostAttachment,
  type PostRow,
  type ReactionEmoji,
} from "@/lib/types";
import { cn } from "@/lib/utils";

import { NotFound } from "./DocumentDetail";

/** /forum/threads/:id — header, body, post stream, reply composer.
 *  Live updates via /ws/forum/thread/<id>/ (consumer-side push). */
export default function ThreadDetail() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id ? params.id : undefined;

  const { data: user } = useCurrentUser();
  const { data: thread, isLoading: tLoading, error: tError } = useThread(id);
  const { data: posts,  isLoading: pLoading } = usePosts(id);

  // Thread WS multiplexes post.new/deleted (auto-refetches the list)
  // AND typing.start/stop (live "X is typing…" chip in the composer).
  const threadSocket = useThreadSocket(id);

  const pin         = usePinThread();
  const lock        = useLockThread();
  const del         = useDeleteThread();
  const subscribe   = useSubscribeThread();
  const unsubscribe = useUnsubscribeThread();
  const confirm     = useConfirm();

  // Lifted so any PostCard can open the dialog; null = closed.
  const [reportingPostId, setReportingPostId] = useState<string | null>(null);
  // The post we're currently replying to (or null for top-level posts).
  // Lives at the parent so PostCard's Reply button can hand a target up
  // to the composer at the bottom of the page.
  const [replyTo, setReplyTo] = useState<PostRow | null>(null);

  // Measured height of the sticky composer at the bottom. Drives the
  // post list's bottom padding so the freshest reply never gets hidden
  // behind the composer. Re-measured via ResizeObserver whenever the
  // composer grows (attaching files, multi-line content, reply-to
  // context block). Falls back to a sensible default until the first
  // measurement lands.
  const composerOuterRef = useRef<HTMLDivElement | null>(null);
  const [composerHeight, setComposerHeight] = useState(180);
  useEffect(() => {
    const el = composerOuterRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const h = Math.ceil(entries[0]?.contentRect.height ?? 0);
      // +24px breathing room so the last bubble isn't touching the
      // composer's top edge.
      if (h > 0) setComposerHeight(h + 24);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- Auto-scroll to newest message ----------------------------------
  // <main> is the scrollable ancestor (set in AppLayout). We climb the
  // DOM from a post-list anchor to find it, then control its scrollTop.
  // Three triggers:
  //   1. First time posts arrive   → ALWAYS jump to bottom (open the
  //      thread at the newest reply, like every chat app).
  //   2. New post from someone else → jump only if the user is still
  //      near the bottom (within 200 px), so reading history isn't
  //      yanked away.
  //   3. After the user sends      → ALWAYS jump (called explicitly
  //      from the composer's onSuccess via scrollToBottomNow).
  const postsScrollRef = useRef<HTMLDivElement | null>(null);
  const didInitialScrollRef = useRef(false);
  const postsLength = posts?.results.length ?? 0;

  // Find <main> (or whichever ancestor is the scrollport).
  function findScrollEl(): HTMLElement | null {
    const anchor = postsScrollRef.current;
    if (!anchor) return null;
    let el: HTMLElement | null = anchor.parentElement;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      if (style.overflowY === "auto" || style.overflowY === "scroll") return el;
      el = el.parentElement;
    }
    return null;
  }

  // Force-scroll, used by the composer after a successful send.
  const scrollToBottomNow = useCallback(() => {
    // rAF lets the new message render before we measure scrollHeight.
    requestAnimationFrame(() => {
      const el = findScrollEl();
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  useEffect(() => {
    if (postsLength === 0) return;
    const el = findScrollEl();
    if (!el) return;
    // rAF so the layout has painted the new post before measuring.
    requestAnimationFrame(() => {
      if (!didInitialScrollRef.current) {
        // First arrival → unconditional jump to bottom.
        el.scrollTop = el.scrollHeight;
        didInitialScrollRef.current = true;
        return;
      }
      // Otherwise, respect user scroll-up.
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < 200) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }, [postsLength]);

  if (tLoading) return <ThreadSkeleton />;
  if (tError || !thread) return <NotFound back="/forum" label="forum" />;

  const isMod   = !!user?.is_director_level;
  const isOwner = !!user && user.id === thread.author_id;
  const canMod  = isMod || isOwner;

  async function onDelete() {
    if (!thread) return;
    const ok = await confirm({
      title: `Delete "${thread.title}"?`,
      description: "Every post in it goes too.",
      confirmLabel: "Delete thread",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(thread.id, { onSuccess: () => navigate("/forum") });
  }

  return (
    <div className="mx-auto max-w-3xl space-y-3">
      {/* ---------- Telegram-style chat header — single thin row ----------
          Sticky to the very top of <main> so it stays pinned while
          messages scroll behind it. The negative `top` cascade matches
          <main>'s p-4 / sm:p-6 / lg:p-8 padding-top, so the sticky
          anchor lands at the actual viewport edge instead of one
          padding band below it. z-30 keeps it above the composer's
          z-20 in case they ever overlap. */}
      <Card className="sticky -top-4 z-30 rounded-none border-x-0 border-t-0 border-border/60 shadow-soft sm:-top-6 lg:-top-8">
        <CardContent className="flex items-center gap-3 p-2.5">
          <Button asChild variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Back to forum">
            <Link to="/forum" aria-label="Back to forum">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>

          {/* Thread avatar — initials of the title (Telegram-style group
              icon). Not the author's photo, because the header represents
              the THREAD, not the creator; the author still appears in the
              subtitle and on their own posts below. `initials()` returns
              two characters: first+last for multi-word titles, first two
              chars for single-word titles. */}
          <Avatar className="h-10 w-10 shrink-0 ring-1 ring-border">
            <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-sm font-semibold uppercase text-primary-foreground">
              {initials(thread.title)}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h1 className="truncate text-base font-semibold tracking-tight">{thread.title}</h1>
              {thread.is_pinned && (
                <Pin className="h-3 w-3 shrink-0 fill-amber-400 text-amber-500" aria-label="Pinned" />
              )}
              {thread.is_locked && (
                <Lock className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Locked" />
              )}
            </div>
            <div className="truncate text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground/80">{thread.category_display}</span>
              {" · "}
              {thread.author_name || "—"}
              {" · "}{formatDateTime(thread.created_at)}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <OnlineUsersBadge compact />
            <Button
              variant={thread.subscribed ? "secondary" : "ghost"}
              size="icon"
              className="h-8 w-8"
              disabled={subscribe.isPending || unsubscribe.isPending}
              title={thread.subscribed ? "Unsubscribe from this thread" : "Subscribe to this thread"}
              aria-label={thread.subscribed ? "Unsubscribe from this thread" : "Subscribe to this thread"}
              onClick={() =>
                thread.subscribed
                  ? unsubscribe.mutate(thread.id)
                  : subscribe.mutate(thread.id)
              }
            >
              {subscribe.isPending || unsubscribe.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : thread.subscribed ? (
                <BellOff className="h-4 w-4" />
              ) : (
                <Bell className="h-4 w-4" />
              )}
            </Button>
            {canMod && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title="Manage thread"
                  aria-label="Manage thread"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {isMod && (
                  <>
                    <DropdownMenuItem onSelect={() => pin.mutate(thread.id)}>
                      <Pin className={cn("mr-2 h-4 w-4", thread.is_pinned && "fill-amber-400 text-amber-500")} />
                      {thread.is_pinned ? "Unpin thread" : "Pin to top"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => lock.mutate(thread.id)}>
                      {thread.is_locked
                        ? <><ShieldOff className="mr-2 h-4 w-4" />Unlock thread</>
                        : <><Lock className="mr-2 h-4 w-4" />Lock thread</>}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={del.isPending}
                  onSelect={(e) => { e.preventDefault(); onDelete(); }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {del.isPending ? "Deleting…" : "Delete thread"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ---------- Thread body (only if present) ----------
          Kept as its own slim block so the header above stays one row
          tall. Most threads in this app are pure discussion (no body),
          in which case this section doesn't render at all. */}
      {thread.body && (
        <Card className="border-border/60">
          <CardContent
            className="prose prose-sm max-w-none whitespace-pre-wrap p-4 text-sm text-foreground"
            dangerouslySetInnerHTML={{ __html: thread.body }}
          />
        </Card>
      )}

      {/* ---------- Posts (flow naturally in the page) ----------
          No bounded panel — the post stream just flows down the page,
          and the page itself scrolls. The sticky composer below stays
          pinned to the viewport bottom, so messages scroll out of view
          behind it (against the page background, not into the
          composer card). Padding-bottom on this list keeps the last
          message clear of the composer's height so users can always
          read the freshest reply without the composer overlapping. */}
      <div
        ref={postsScrollRef}
        className="space-y-3"
        style={{ paddingBottom: composerHeight }}
      >
        {pLoading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)
        ) : posts && posts.results.length > 0 ? (
          posts.results.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              onReport={() => setReportingPostId(p.id)}
              onReply={() => setReplyTo(p)}
            />
          ))
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No replies yet. Be the first to respond.
            </CardContent>
          </Card>
        )}
      </div>

      {/* ---------- Typing indicator + composer (sticky to viewport) ----------
          Now that posts live in their own bounded scroll box above, the
          composer can safely stick to the bottom of <main> without any
          message text passing behind it. A solid bg-background is fine
          — no blur/opacity tricks needed, because the post list can't
          reach into this area.
          Negative side margins + matching padding extend the bar past
          the page gutters so the page background colour doesn't peek
          through on the edges. */}
      {/* Sticky bottom — the negative `bottom` offset is what actually
          parks the composer flush against the viewport edge.
          `sticky bottom-0` would anchor at the scrollport's inner
          content edge (inside <main>'s padding-box), leaving a
          padding-sized dead band below. A NEGATIVE bottom offset
          pushes the sticky anchor down INTO <main>'s padding zone, so
          the wrapper's bottom edge ends up at the actual viewport
          bottom. The cascade matches <main>'s p-4/sm:p-6/lg:p-8.
          pb-0 keeps the composer card flush with the wrapper's
          bottom — no internal dead space either. */}
      <div
        ref={composerOuterRef}
        className="sticky -bottom-4 z-20 -mx-4 space-y-2 bg-background px-4 pb-0 pt-3 sm:-bottom-6 sm:-mx-6 sm:px-6 lg:-bottom-8 lg:-mx-8 lg:px-8"
      >
        {threadSocket.typingUsers.length > 0 && (
          <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
            <span className="inline-flex gap-0.5">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary"></span>
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary [animation-delay:120ms]"></span>
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary [animation-delay:240ms]"></span>
            </span>
            {threadSocket.typingUsers.length === 1
              ? <><span className="font-medium text-foreground">{threadSocket.typingUsers[0].name}</span> is typing…</>
              : threadSocket.typingUsers.length === 2
                ? <><span className="font-medium text-foreground">{threadSocket.typingUsers[0].name}</span> and <span className="font-medium text-foreground">{threadSocket.typingUsers[1].name}</span> are typing…</>
                : <><span className="font-medium text-foreground">{threadSocket.typingUsers.length} people</span> are typing…</>
            }
          </div>
        )}

        {thread.is_locked && !isMod ? (
          <Card className="border-border bg-muted/40">
            <CardContent className="flex items-center gap-2 p-4 text-sm text-foreground/80">
              <Lock className="h-4 w-4" />
              This thread is locked. Only moderators can post.
            </CardContent>
          </Card>
        ) : (
          <ReplyComposer
            threadId={thread.id}
            replyTo={replyTo}
            onClearReply={() => setReplyTo(null)}
            sendTyping={threadSocket.sendTyping}
            onSent={scrollToBottomNow}
          />
        )}
      </div>

      <ReportPostDialog
        postId={reportingPostId}
        onOpenChange={(o) => !o && setReportingPostId(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reply composer (new post)
// ---------------------------------------------------------------------------

function ReplyComposer({
  threadId, replyTo, onClearReply, sendTyping, onSent,
}: {
  threadId: string;
  replyTo: PostRow | null;
  onClearReply: () => void;
  /** From useThreadSocket — broadcasts typing.start/stop. The receiving
   *  "X is typing…" chip lives in ThreadDetail above this composer. */
  sendTyping: (typing: boolean) => void;
  /** Called once the new post has been accepted by the server so the
   *  parent can scroll the page to the bottom and reveal it. */
  onSent: () => void;
}) {
  const create = useCreatePost();
  const [content, setContent] = useState("");
  const [files,   setFiles]   = useState<File[]>([]);
  // null = idle, -1 = uploading but total unknown, 0–100 = percent.
  // UploadProgressBar reads the same convention, so we can pass through.
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileInputRef  = useRef<HTMLInputElement | null>(null);
  const composerRef   = useRef<HTMLDivElement | null>(null);
  const textareaRef   = useRef<HTMLTextAreaElement | null>(null);
  // Used by the Enter-to-send shortcut so we can call requestSubmit()
  // and reuse the form's normal onSubmit path (validation, mutation, etc).
  const formRef       = useRef<HTMLFormElement | null>(null);
  // Throttle typing pings — server gets at most one start per 2s, and a
  // single stop after 2.5s of no keystrokes.
  const lastTypingPing = useRef<number>(0);
  const stopTimer      = useRef<number | null>(null);

  function pingTyping() {
    const now = Date.now();
    if (now - lastTypingPing.current > 2000) {
      sendTyping(true);
      lastTypingPing.current = now;
    }
    if (stopTimer.current) window.clearTimeout(stopTimer.current);
    stopTimer.current = window.setTimeout(() => {
      sendTyping(false);
      lastTypingPing.current = 0;
    }, 2500);
  }

  // On unmount/composer-clear → emit a final stop so other users don't
  // see a ghost chip.
  useEffect(() => () => {
    if (stopTimer.current) window.clearTimeout(stopTimer.current);
    sendTyping(false);
  }, [sendTyping]);

  // When the user clicks Reply on a post elsewhere on the page, bring
  // the composer into view + focus the textarea so they can start typing
  // immediately. Only fires when the target id actually changes.
  useEffect(() => {
    if (replyTo) {
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      textareaRef.current?.focus();
    }
  }, [replyTo?.id]);

  // Send is allowed whenever there's something to send. Don't gate on
  // create.isPending OR uploadProgress — both have caused "silent
  // lockouts" where the user couldn't send anymore after a single
  // stuck mutation/upload. React Query dedupes overlapping JSON
  // mutations; for multipart, a double-tap might cost one extra POST
  // but won't permanently brick the composer.
  const canSubmit = content.trim().length > 0 || files.length > 0;
  // Pure UI signal for spinner / disabled-on-attach.
  const isSending = create.isPending || uploadProgress !== null;

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length) setFiles((prev) => [...prev, ...picked]);
    // Reset so the same file can be re-picked after being removed.
    e.target.value = "";
  }

  function removeFile(i: number) {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    submitNow();
  }

  // Enter sends · Shift+Enter / Ctrl+Enter / Cmd+Enter insert a newline.
  // MentionTextarea forwards onKeyDown to us *only* when its own popup
  // isn't intercepting Enter (for picking a mention), so this is safe.
  // Calls submitNow() directly rather than the form's requestSubmit() —
  // one less indirection means one fewer thing that can silently fail
  // (some browsers also refuse requestSubmit when the form isn't focused).
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== "Enter") return;
    // Any modifier → let the textarea insert a newline as usual.
    if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    // Don't fight the IME composition flow (CJK input, accents, etc).
    if (e.nativeEvent.isComposing) return;
    e.preventDefault();
    submitNow();
  }

  /** Shared submit path used by both the form's onSubmit *and* the
   *  Enter-key shortcut. Console-traced so a missing "[composer]"
   *  line in DevTools means the keypress/click never reached this
   *  function (stale HMR or focus theft). */
  function submitNow() {
    // eslint-disable-next-line no-console
    console.log("[composer] submitNow", {
      canSubmit, contentLen: content.length, files: files.length,
      uploadProgress, isPending: create.isPending,
    });
    if (!canSubmit) return;
    // null while not uploading, -1 if total is unknown, 0-100 otherwise.
    if (files.length > 0) setUploadProgress(-1);
    create.mutate(
      {
        thread: threadId,
        content: content.trim(),
        files: files.length ? files : undefined,
        reply_to: replyTo?.id ?? undefined,
        onUploadProgress: setUploadProgress,
      },
      {
        onSuccess: (post) => {
          // eslint-disable-next-line no-console
          console.log("[composer] post.created", post?.id);
          setContent("");
          setFiles([]);
          setUploadProgress(null);
          onClearReply();
          // Reveal the freshly-posted reply at the bottom of the page.
          try { onSent(); } catch { /* never block the success flow */ }
        },
        onError: (err) => {
          // eslint-disable-next-line no-console
          console.error("[composer] post.failed", err);
          // Clear the bar so the user isn't stuck looking at "100%"
          // when the server actually rejected the upload — the toast /
          // error from React Query will surface the failure separately.
          setUploadProgress(null);
        },
      },
    );
  }

  return (
    <Card ref={composerRef} className="border-border/60 shadow-soft">
      <CardContent className="p-4">
        <form ref={formRef} onSubmit={onSubmit} className="space-y-3">
          {replyTo && (
            <div className="flex items-start gap-2 rounded-md border-l-2 border-primary bg-primary/5 px-3 py-2 text-xs">
              <CornerUpLeft className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-foreground">
                  Replying to {replyTo.author_name || "—"}
                </div>
                <div className="mt-0.5 truncate text-muted-foreground">
                  {(replyTo.content || "").slice(0, 160) || "(empty)"}
                </div>
              </div>
              <button
                type="button"
                onClick={onClearReply}
                className="rounded-sm p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                aria-label="Cancel reply"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <MentionTextarea
            ref={textareaRef}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              // Only ping while there's actually text — clearing the
              // textarea sends an immediate stop via the unmount/effect
              // cleanup pattern above.
              if (e.target.value.trim()) pingTyping();
            }}
            onKeyDown={handleKeyDown}
            placeholder={replyTo ? `Reply to ${replyTo.author_name}…` : "Write a reply…"}
            rows={3}
          />

          {/* Live upload progress — shown only while a multipart POST is
              in flight. Reuses the same UploadProgressBar the document
              library uses so the visual language is consistent. */}
          {uploadProgress !== null && files.length > 0 && (
            <UploadProgressBar
              progress={uploadProgress}
              totalBytes={files.reduce((s, f) => s + f.size, 0)}
            />
          )}

          {files.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 py-1 pl-2.5 pr-1 text-xs"
                >
                  <Paperclip className="h-3 w-3 text-muted-foreground" />
                  <span className="max-w-[12rem] truncate">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="ml-1 rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                    aria-label={`Remove ${f.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={onPickFiles}
          />

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isSending}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="mr-2 h-4 w-4" />Attach
              </Button>
              <span className="hidden text-xs text-muted-foreground sm:inline">
                <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Enter</kbd> to send ·
                {" "}<kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Shift+Enter</kbd> for a new line ·
                {" "}<code className="rounded bg-muted px-1">@firstname</code> to ping
              </span>
            </div>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              {isSending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Posting…</>
                : <><Send className="mr-2 h-4 w-4" />Reply</>}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Per-post card with inline edit + delete
// ---------------------------------------------------------------------------

function PostCard({
  post, onReport, onReply,
}: {
  post: PostRow;
  onReport: () => void;
  onReply: () => void;
}) {
  const { data: user } = useCurrentUser();
  const update  = useUpdatePost();
  const del     = useDeletePost();
  const react   = useReactToPost();
  const confirm = useConfirm();

  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(post.content);

  const isMine     = !!user && user.id === post.author_id;
  // Author-only delete — mods moderate other people's posts via the
  // Report flow (hidden via POST /posts/<id>/hide/), not by impersonating
  // an author-delete here.
  const canDel     = isMine;
  const canReport  = !!user && !isMine;
  const canReply   = !!user;
  const showMenu   = canReport || canDel || post.can_edit || canReply;

  function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    update.mutate(
      { id: post.id, thread: post.thread, content: draft.trim() },
      { onSuccess: () => setEditing(false) },
    );
  }

  async function onDelete() {
    const ok = await confirm({
      title: "Delete this post?",
      description: "Mods can restore it later.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate({ id: post.id, thread: post.thread });
  }

  // Hidden posts render as a moderator stub so the audit trail stays
  // visible without exposing the body.
  if (post.is_hidden) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/40/50 px-4 py-2 text-xs text-muted-foreground">
        <ShieldOff className="mr-1.5 inline h-3.5 w-3.5" />
        A moderator hid this post.
      </div>
    );
  }

  // Chat layout: mine align right (avatar after bubble), others align left.
  // The bubble itself is the colored block; meta + reactions sit below it.
  return (
    <div
      id={`post-${post.id}`}
      className={cn("flex w-full gap-2", isMine ? "flex-row-reverse" : "flex-row")}
    >
      <PresenceAvatar
        userId={post.author_id}
        imageUrl={post.author?.avatar_url}
        name={post.author_name}
        size="sm"
      />

      <div className={cn("flex max-w-[80%] min-w-0 flex-col sm:max-w-[75%]", isMine ? "items-end" : "items-start")}>
        {/* Author name + timestamp (skip "you" for own posts). */}
        <div className="mb-1 flex items-center gap-2 px-1 text-[10px] text-muted-foreground">
          {!isMine && (
            <span className="font-semibold text-foreground">{post.author_name || "—"}</span>
          )}
          <span>{formatDateTime(post.created_at)}</span>
          {post.is_edited && <span>(edited)</span>}
        </div>

        {/* The bubble. Colour + sharper-corner side flip per author. */}
        <div
          className={cn(
            "relative rounded-2xl px-4 py-2.5 text-sm shadow-soft",
            isMine
              ? "rounded-tr-md bg-primary text-primary-foreground"
              : "rounded-tl-md bg-card border border-border/60",
          )}
        >
          {/* Quoted parent — show when this post is a reply. Tapping it
              jumps to the parent (uses the id we set on the wrapper). */}
          {post.reply_to_preview && (
            <a
              href={`#post-${post.reply_to_preview.id}`}
              className={cn(
                "mb-2 block rounded-md border-l-2 px-2 py-1.5 text-xs no-underline",
                isMine
                  ? "border-primary-foreground/40 bg-primary-foreground/10 text-primary-foreground/90"
                  : "border-primary/40 bg-primary/5 text-foreground",
              )}
            >
              <div className="flex items-center gap-1 font-semibold">
                <CornerUpLeft className="h-3 w-3" />
                {post.reply_to_preview.author_name}
              </div>
              <div className="mt-0.5 line-clamp-2">
                {post.reply_to_preview.is_hidden
                  ? <em>(hidden by a moderator)</em>
                  : post.reply_to_preview.excerpt || "(empty)"}
              </div>
            </a>
          )}

          {editing ? (
            <form onSubmit={onSave} className="space-y-2">
              <MentionTextarea
                rows={3}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className={isMine ? "bg-primary-foreground/95 text-foreground" : undefined}
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant={isMine ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setEditing(false)}
                >
                  <X className="mr-1 h-3.5 w-3.5" />Cancel
                </Button>
                <Button
                  type="submit" size="sm"
                  variant={isMine ? "secondary" : "default"}
                  disabled={!draft.trim() || update.isPending}
                >
                  {update.isPending
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Check className="mr-1 h-3.5 w-3.5" />}
                  Save
                </Button>
              </div>
            </form>
          ) : (
            <>
              {post.content && (
                <p className="whitespace-pre-wrap leading-relaxed">
                  {renderWithMentions(post.content, isMine)}
                </p>
              )}
              {post.attachments.length > 0 && (
                <AttachmentList attachments={post.attachments} isMine={isMine} />
              )}
            </>
          )}
        </div>

        {/* Reactions live below the bubble so the bubble stays clean. */}
        {!editing && !!user && (
          <ReactionBar
            post={post}
            onReact={(emoji) =>
              react.mutate({ postId: post.id, thread: post.thread, emoji })
            }
            busy={react.isPending}
            align={isMine ? "end" : "start"}
          />
        )}
      </div>

      {/* Kebab — quick reply + edit / report / delete. Hovers next to
          the bubble; on small screens it stays visible. */}
      {showMenu && !editing && (
        <div className="self-start pt-5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isMine ? "start" : "end"} className="w-40">
              {canReply && (
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); onReply(); }}>
                  <ReplyIcon className="mr-2 h-4 w-4" />Reply
                </DropdownMenuItem>
              )}
              {post.can_edit && (
                <DropdownMenuItem onSelect={() => { setDraft(post.content); setEditing(true); }}>
                  <Pencil className="mr-2 h-4 w-4" />Edit
                </DropdownMenuItem>
              )}
              {canReport && (
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); onReport(); }}>
                  <Flag className="mr-2 h-4 w-4" />Report
                </DropdownMenuItem>
              )}
              {canDel && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={del.isPending}
                  onSelect={(e) => { e.preventDefault(); onDelete(); }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {del.isPending ? "Deleting…" : "Delete"}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

function AttachmentList({
  attachments, isMine = false,
}: {
  attachments: PostAttachment[];
  isMine?: boolean;
}) {
  const images = attachments.filter((a) => a.is_image);
  const files  = attachments.filter((a) => !a.is_image);

  return (
    <div className="mt-2 space-y-2">
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {images.map((a) => (
            <a
              key={a.id}
              href={a.file_url ?? "#"}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "group block overflow-hidden rounded-md border",
                isMine ? "border-primary-foreground/20 bg-primary-foreground/10" : "bg-muted",
              )}
            >
              <img
                src={a.file_url ?? ""}
                alt={a.original_name}
                loading="lazy"
                className="aspect-video h-full w-full object-cover transition group-hover:opacity-90"
              />
            </a>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((a) => (
            <li key={a.id}>
              <a
                href={a.file_url ?? "#"}
                target="_blank"
                rel="noreferrer"
                className={cn(
                  "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm",
                  isMine
                    ? "border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/15"
                    : "border-border/60 bg-card hover:bg-accent/40",
                )}
              >
                <FileText className={cn("h-4 w-4", isMine ? "text-primary-foreground/80" : "text-muted-foreground")} />
                <span className="font-medium">{a.original_name}</span>
                <span className={cn("text-xs", isMine ? "text-primary-foreground/70" : "text-muted-foreground")}>
                  {a.human_size}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReactionBar({
  post, onReact, busy, align = "start",
}: {
  post: PostRow;
  onReact: (emoji: ReactionEmoji) => void;
  busy: boolean;
  align?: "start" | "end";
}) {
  // Server already returns only emojis with count >= 1, in canonical
  // order — render them directly. Telegram-style: tapping an existing
  // chip toggles the actor's own reaction off/on; the "+" button opens
  // the full picker for adding a new emoji.
  const used = post.reactions;

  return (
    <div className={cn(
      "mt-1 flex flex-wrap items-center gap-1.5 px-1",
      align === "end" ? "justify-end" : "justify-start",
    )}>
      {used.map((r) => (
        <button
          key={r.emoji}
          type="button"
          disabled={busy}
          onClick={() => onReact(r.emoji as ReactionEmoji)}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition",
            r.by_me
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border/60 bg-muted/30 text-muted-foreground hover:bg-muted/60",
            busy && "opacity-60",
          )}
          aria-pressed={r.by_me}
          aria-label={`React with ${r.emoji}`}
          title={r.by_me ? "Remove your reaction" : `React with ${r.emoji}`}
        >
          <span className="text-sm leading-none">{r.emoji}</span>
          <span className="font-mono tabular-nums">{r.count}</span>
        </button>
      ))}

      {/* "Add reaction" picker — full catalog grouped by category.
          Quick row at the top with the 5 most-tapped emojis, then
          scrollable sections (Smileys / Gestures / Hearts / Symbols)
          below. The catalog comes from the server so adding an emoji
          on the backend lights it up here automatically. */}
      <ReactionPicker
        align={align}
        busy={busy}
        onPick={(emoji) => onReact(emoji)}
      />
    </div>
  );
}

function ReactionPicker({
  align, busy, onPick,
}: {
  align: "start" | "end";
  busy: boolean;
  onPick: (emoji: string) => void;
}) {
  const { data } = useReactionsCatalog();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={busy}
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-full border border-dashed text-muted-foreground transition",
            "hover:border-solid hover:bg-muted/60 hover:text-foreground",
            busy && "opacity-60",
          )}
          aria-label="Add reaction"
          title="Add reaction"
        >
          <SmilePlus className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align === "end" ? "end" : "start"}
        className="w-[280px] p-2"
      >
        {/* Quick row — the five everyday emojis, big tappable cells. */}
        <div className="mb-1 flex gap-0.5 border-b border-border/60 pb-2">
          {REACTION_EMOJIS.map((emoji) => (
            <button
              key={`quick-${emoji}`}
              type="button"
              onClick={() => onPick(emoji)}
              className="flex-1 rounded p-1.5 text-xl leading-none transition hover:scale-110 hover:bg-accent"
              aria-label={`React with ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>

        {/* Categories — server-driven; loops EMOJI_GROUPS straight from
            the catalog endpoint so adding a server-side emoji shows up
            here without a frontend change. */}
        <div className="max-h-[260px] overflow-y-auto pr-1">
          {(data?.groups ?? []).map((g) => (
            <section key={g.name} className="mt-2 first:mt-0">
              <h4 className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {g.name}
              </h4>
              <div className="grid grid-cols-7 gap-0.5">
                {g.emojis.map((emoji) => (
                  <button
                    key={`${g.name}-${emoji}`}
                    type="button"
                    onClick={() => onPick(emoji)}
                    className="rounded p-1.5 text-lg leading-none transition hover:scale-110 hover:bg-accent"
                    aria-label={`React with ${emoji}`}
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Mirrors the backend MENTION_RE; purely visual — backend remains the
// source of truth for who actually gets notified.
const MENTION_RE = /(?<!\w)@([a-zA-Z][a-zA-Z0-9.\-]{1,40})/g;

function renderWithMentions(text: string, isMine = false): React.ReactNode {
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let i = 0;
  // On own (primary-coloured) bubbles, `text-primary` blends in with
  // `bg-primary`; flip to a contrast pair that reads against the bubble.
  const mentionCls = isMine
    ? "rounded bg-primary-foreground/20 px-1 py-px font-semibold text-primary-foreground underline decoration-primary-foreground/50 decoration-1 underline-offset-2"
    : "rounded bg-primary/10 px-1 py-px font-medium text-primary";
  for (const match of text.matchAll(MENTION_RE)) {
    const start = match.index ?? 0;
    if (start > lastIndex) out.push(text.slice(lastIndex, start));
    out.push(
      <span key={`m-${i++}`} className={mentionCls}>
        @{match[1]}
      </span>,
    );
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}

function ThreadSkeleton() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PresenceAvatar — Avatar + a small green/grey dot in the corner that
// reflects whether the user has an open WS connection. Used inside
// ThreadDetail; could be lifted to a shared component later.
// ---------------------------------------------------------------------------
function PresenceAvatar({
  userId, imageUrl, name, size = "sm",
}: {
  userId: string | null;
  imageUrl: string | null | undefined;
  name: string | null;
  size?: "sm" | "md";
}) {
  const { isOnline } = usePresence();
  const online = isOnline(userId);
  const dim = size === "md" ? "h-10 w-10" : "h-8 w-8";
  const text = size === "md" ? "text-xs" : "text-[10px]";
  return (
    // `self-start` is load-bearing: without it, this wrapper stretches
    // to the flex parent's full height (default align-items: stretch),
    // and the absolute-positioned dot anchors to the bottom of that
    // stretched column instead of the avatar's corner. `inline-block`
    // gives it intrinsic sizing on the off-chance it's used outside a
    // flex parent.
    <div className="relative inline-block shrink-0 self-start">
      <Avatar className={cn(dim, "ring-1 ring-border")}>
        {imageUrl && <AvatarImage src={imageUrl} alt="" />}
        <AvatarFallback className={cn(
          "bg-gradient-to-br from-primary to-emerald-700 font-semibold text-primary-foreground",
          text,
        )}>
          {initials(name || "")}
        </AvatarFallback>
      </Avatar>
      {/* Green dot when online, grey ring-bordered dot when not. Always
          rendered so the avatar's positioning stays stable. */}
      <span
        className={cn(
          "absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-card",
          online ? "bg-emerald-500" : "bg-slate-400/70",
        )}
        title={online ? `${name ?? "User"} is online` : `${name ?? "User"} is offline`}
        aria-label={online ? "Online" : "Offline"}
      />
    </div>
  );
}
